import { type CollisionDetection, closestCenter, rectIntersection } from '@dnd-kit/core';

/**
 * Custom collision detection that prioritizes a droppable area when hovering near it,
 * but allows replace actions when hovering directly over other droppable items with significant overlap.
 * Uses spatial awareness and a forgiving detection zone to create clear zones for each action.
 *
 * This algorithm is designed for scenarios where you have:
 * - A large droppable area (e.g., "add to list" zone)
 * - Individual droppable items within or near that area (e.g., "replace item" targets)
 *
 * Features:
 * - Uses rectIntersection for more forgiving collision detection (not just pointer position)
 * - Adds 20px vertical margin above/below drop area for easier targeting
 * - Requires 75% overlap with carousel items before activating replace action
 * - Prioritizes drop area when dragging near its upper portion
 * - Allows item replacement only when hovering directly over items with significant overlap
 *
 * @param dropAreaPrefix - The ID prefix for the droppable area to prioritize (e.g., 'carousel-drop-area')
 * @param itemPrefix - The ID prefix for individual droppable items (e.g., 'carousel-')
 * @param spatialThreshold - The threshold (0-1) for preferring the drop area when both collide (default: 0.5)
 *                           0.5 means the upper 50% of the drop area is strongly preferred
 *
 * @example
 * ```tsx
 * const collisionDetection = createPrioritizedDropAreaCollision('carousel-drop-area', 'carousel-', 0.5);
 *
 * <DndContext collisionDetection={collisionDetection}>
 *   // Your drag and drop components
 * </DndContext>
 * ```
 */
export function createPrioritizedDropAreaCollision(
  dropAreaPrefix: string,
  itemPrefix: string,
  spatialThreshold = 0.5,
): CollisionDetection {
  return (args) => {
    const { droppableRects, pointerCoordinates, collisionRect } = args;

    // Use rectIntersection first to get all nearby collisions (more forgiving than pointerWithin)
    const nearbyCollisions = rectIntersection(args);

    // Check if we have the DroppableArea in nearby collisions
    const dropAreaCollision = nearbyCollisions.find((collision) => String(collision.id).startsWith(dropAreaPrefix));

    // Filter carousel items to only include those with >= 75% overlap
    const itemCollisions = nearbyCollisions
      .filter(
        (collision) => String(collision.id).startsWith(itemPrefix) && !String(collision.id).startsWith(dropAreaPrefix),
      )
      .filter((collision) => {
        if (!collisionRect) return false;

        const itemRect = droppableRects.get(collision.id);
        if (!itemRect) return false;

        // Calculate intersection area
        const intersectionLeft = Math.max(collisionRect.left, itemRect.left);
        const intersectionTop = Math.max(collisionRect.top, itemRect.top);
        const intersectionRight = Math.min(collisionRect.right, itemRect.right);
        const intersectionBottom = Math.min(collisionRect.bottom, itemRect.bottom);

        // Check if there's actually an intersection
        if (intersectionRight <= intersectionLeft || intersectionBottom <= intersectionTop) {
          return false;
        }

        const intersectionArea = (intersectionRight - intersectionLeft) * (intersectionBottom - intersectionTop);
        const collisionRectArea = collisionRect.width * collisionRect.height;

        // Require 75% overlap
        const overlapPercentage = intersectionArea / collisionRectArea;
        return overlapPercentage >= 0.75;
      });

    // If we have the drop area in collisions, check if we should prioritize it
    if (dropAreaCollision && pointerCoordinates) {
      const dropAreaRect = droppableRects.get(dropAreaCollision.id);

      if (dropAreaRect) {
        // Calculate vertical distance from pointer to drop area
        const pointerY = pointerCoordinates.y;
        const dropAreaTop = dropAreaRect.top;
        const dropAreaBottom = dropAreaRect.bottom;

        // If pointer is within or slightly above/below the drop area vertically
        const verticalMargin = 20; // 20px margin for "forgiveness"
        const isNearDropAreaVertically =
          pointerY >= dropAreaTop - verticalMargin && pointerY <= dropAreaBottom + verticalMargin;

        if (isNearDropAreaVertically) {
          // If we also have item collisions, use spatial logic
          if (itemCollisions.length > 0) {
            const pointerYInDropArea = pointerY - dropAreaTop;
            const dropAreaHeight = dropAreaRect.height;
            const preferDropAreaThreshold = dropAreaHeight * spatialThreshold;

            // Prefer drop area if in upper portion
            if (pointerYInDropArea < preferDropAreaThreshold) {
              return [dropAreaCollision];
            }
            // Otherwise prefer item collisions
            return itemCollisions;
          }

          // No item collisions, just return drop area
          return [dropAreaCollision];
        }
      }
    }

    // If only droppable items are detected, return those
    if (itemCollisions.length > 0) {
      return itemCollisions;
    }

    // If we found the drop area but failed spatial checks, still return it if nothing else
    if (dropAreaCollision) {
      return [dropAreaCollision];
    }

    // For keyboard navigation or when no rect intersections found,
    // use closestCenter to find the nearest droppable (only if collisionRect exists)
    if (collisionRect) {
      const closestCenterCollisions = closestCenter(args);

      if (closestCenterCollisions.length > 0) {
        return closestCenterCollisions;
      }
    }

    // Final fallback - should rarely reach here
    return [];
  };
}
